import React from 'react';
import { ArrowLeft, Zap, Users, Wrench, Factory, Shield, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { companyInfo } from '../mockData';

const missionSections = [
  {
    title: 'The Industry Challenge',
    text: "In the electrical contracting industry, customers often face a multitude of challenges when seeking comprehensive electrical solutions. Many contractors specialize in only one area, such as residential or commercial, leaving clients with complex needs struggling to find a single provider capable of addressing all their requirements. This fragmentation leads to increased costs, extended project timelines, and potential miscommunication between multiple contractors.",
    image: 'https://images.unsplash.com/photo-1660330590022-9f4ff56b63f6?w=1600',
    icon: Zap
  },
  {
    title: 'Who We Are',
    text: "At Ol' McToodles Electric (OME), we recognize these pain points and aim to resolve them by offering a wide range of electrical services under one roof. As a local family-based business, we understand the diverse needs of residential, commercial, and industrial clients in our community. Our expertise has evolved from humble beginnings in low-voltage cable installation for HVAC automation to a comprehensive suite of electrical services.",
    image: 'https://images.unsplash.com/photo-1770838773181-e1b17ec22fee?w=1600',
    icon: Users
  },
  {
    title: 'Where It All Began',
    text: "The journey began with entry-level work, pulling low-voltage cables through commercial and industrial facilities for HVAC automation and new installations. This experience led to HVAC automation design work and drafting responsibilities, including assistance with project RFPs and Estimate submittals. These skillsets then transitioned into residential installations, commercial/industrial facility electrical and fire alarm troubleshooting, verifications, and installations.",
    image: 'https://images.unsplash.com/photo-1622426385889-4fc93a72423a?w=1600',
    icon: Wrench
  },
  {
    title: 'What Sets Us Apart',
    text: "What sets Ol' McToodles Electric apart is our ability to provide end-to-end solutions. This includes in-house trenching and backfilling capabilities (subcontracted Hydro-vac services), as well as Millwright, Welding, and other trades support through subcontracting agreements. Our comprehensive approach eliminates the need for clients to hire multiple contractors, significantly reducing costs and streamlining the entire electrical installation process.",
    image: 'https://images.unsplash.com/photo-1615774925655-a0e97fc85c14?w=1600',
    icon: Shield
  }
];

const closingText = "By offering this all-inclusive service, we not only address the immediate electrical needs of our customers but also tackle the broader issues of project coordination and management, cost management, and quality control that have long plagued the industry. Our unique positioning as a one-stop-shop for electrical and extremity trades contracting services solves a critical problem for clients seeking efficient, cost-effective, and high-quality electrical solutions across various sectors.";

const OurMission = () => {
  const navigate = useNavigate();

  return (
    <div data-testid="our-mission-page" className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1641338575113-54e87ff0c474?w=1600)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-black/80"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 lg:px-8 pt-28 pb-16 text-center">
          <div className="max-w-4xl mx-auto">
            <button
              data-testid="back-to-home"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-gold/80 hover:text-gold mb-8 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Our Mission
            </h1>
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className="h-1 w-16 bg-gold rounded-full"></div>
              <div className="h-2 w-2 bg-gold rounded-full"></div>
              <div className="h-1 w-24 bg-gold rounded-full"></div>
              <div className="h-2 w-2 bg-gold rounded-full"></div>
              <div className="h-1 w-16 bg-gold rounded-full"></div>
            </div>
            <p className="text-lg text-gold font-semibold tracking-wide">
              {companyInfo.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Content Sections */}
      {missionSections.map((section, index) => {
        const Icon = section.icon;
        const isEven = index % 2 === 0;

        return (
          <section
            key={index}
            data-testid={`mission-section-${index}`}
            className="relative min-h-[50vh] flex items-center overflow-hidden"
          >
            {/* Background Image */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${section.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed'
              }}
            >
              <div className={`absolute inset-0 ${
                isEven ? 'bg-black/88' : 'bg-black/90'
              }`}></div>
            </div>

            {/* Content */}
            <div className="relative z-10 container mx-auto px-4 lg:px-8 py-20 lg:py-28">
              <div className={`max-w-4xl ${isEven ? 'mr-auto' : 'ml-auto'}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-gold/15 flex items-center justify-center shrink-0">
                    <Icon className="w-7 h-7 text-gold" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    {section.title}
                  </h2>
                </div>
                <p className="text-base lg:text-lg text-gray-300 leading-relaxed pl-[74px]">
                  {section.text}
                </p>
              </div>
            </div>
          </section>
        );
      })}

      {/* Closing Section */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1600)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-black/90"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 lg:px-8 py-24 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-1 bg-gold mx-auto mb-10 rounded-full"></div>
            <p className="text-base lg:text-lg text-gray-300 leading-relaxed mb-12">
              {closingText}
            </p>
            <button
              data-testid="mission-cta-quote"
              onClick={() => navigate('/contact')}
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold/90 text-black font-bold px-8 py-4 rounded-xl text-lg transition-all duration-200 hover:scale-105"
            >
              Get a Free Quote
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurMission;
